// src/services/authService.supabase.ts
// Supabase-native authentication (replacement for custom backend)

import { logger } from "@/lib/logger";
import { supabase } from "@/lib/supabaseClient";
import type { UserRole } from "@/types/roles";

import {
  getScopedItem,
  setScopedItem,
  removeScopedItem,
} from "../utils/storage";

export interface ClientMembership {
  id: string;
  clientId: string;
  userId: string;
  role: UserRole;
  invitedBy?: string;
  joinedAt: string;
}

export interface AuthUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  name: string;
  role: UserRole;
  roles: string[];
  memberships?: ClientMembership[];
}

export interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

const STORAGE_KEYS = {
  USER: "auth_user",
};

export const OAUTH_REDIRECT_STORAGE_KEY = "auth_oauth_redirect_to";

// Token management is handled by Supabase SDK
export function getAccessToken(): string | null {
  // Supabase manages tokens internally
  return null;
}

export function getRefreshToken(): string | null {
  // Supabase manages tokens internally
  return null;
}

function clearTokens(): void {
  if (typeof window === "undefined") return;
  removeScopedItem(STORAGE_KEYS.USER);
}

// Database role type (matches client_memberships.role constraint)
type DatabaseRole = "owner" | "admin" | "editor" | "viewer";

// Map database roles to frontend UserRole
function mapDatabaseRoleToUserRole(dbRole: DatabaseRole): UserRole {
  // 'owner' and 'admin' both map to 'admin' in the frontend
  if (dbRole === "owner" || dbRole === "admin") return "admin";
  // 'editor' maps to 'member'
  if (dbRole === "editor") return "member";
  // 'viewer' stays as 'viewer'
  return "viewer";
}

// Check if a database role grants admin-level access
function isAdminLevelRole(dbRole: string): boolean {
  return dbRole === "owner" || dbRole === "admin";
}

interface RawMembership {
  id: string;
  client_id: string;
  user_id: string;
  role: DatabaseRole;
  invited_by?: string;
  joined_at: string;
}

async function fetchMembershipsForUser(
  userId: string,
): Promise<{ memberships: ClientMembership[]; hasAdminAccess: boolean }> {
  try {
    const { data, error } = await supabase
      .from("client_memberships")
      .select("*")
      .eq("user_id", userId);

    if (error) {
      logger?.error("Failed to fetch memberships", error);
      return { memberships: [], hasAdminAccess: false };
    }

    const rawData = (data || []) as RawMembership[];

    // Check if any membership has admin-level access BEFORE mapping
    const hasAdminAccess = rawData.some((m) => isAdminLevelRole(m.role));

    const memberships = rawData.map((m) => ({
      id: m.id,
      clientId: m.client_id,
      userId: m.user_id,
      role: mapDatabaseRoleToUserRole(m.role),
      invitedBy: m.invited_by || undefined,
      joinedAt: m.joined_at,
    }));

    return { memberships, hasAdminAccess };
  } catch (error) {
    logger?.error("Error fetching memberships", error);
    return { memberships: [], hasAdminAccess: false };
  }
}

async function buildAuthUser(supabaseUser: any): Promise<AuthUser> {
  // Get user profile
  const { data: profile } = await supabase
    .from("profiles")
    .select("*")
    .eq("id", supabaseUser.id)
    .single();

  // Get memberships (includes pre-computed admin access check)
  const { memberships, hasAdminAccess } = await fetchMembershipsForUser(
    supabaseUser.id,
  );

  // Determine role based on memberships
  // hasAdminAccess is true if user has 'owner' or 'admin' role in any client
  const role: UserRole = hasAdminAccess
    ? "admin"
    : memberships.length > 0
      ? "member"
      : "viewer";

  const firstName = profile?.first_name || "";
  const lastName = profile?.last_name || "";
  const name =
    profile?.display_name ||
    [firstName, lastName].filter(Boolean).join(" ") ||
    supabaseUser.email;

  return {
    id: supabaseUser.id,
    email: supabaseUser.email,
    firstName,
    lastName,
    name,
    role,
    roles: [role],
    memberships,
  };
}

// Authentication functions
export async function login(
  email: string,
  password: string,
): Promise<AuthUser> {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;
    if (!data.user) throw new Error("No user returned");

    const authUser = await buildAuthUser(data.user);

    // Cache user data
    if (typeof window !== "undefined") {
      setScopedItem(STORAGE_KEYS.USER, authUser);
    }

    logger?.info("User logged in successfully", {
      userId: authUser.id,
      email: authUser.email,
    });

    return authUser;
  } catch (error) {
    logger?.error("Login failed", error);
    throw error;
  }
}

export function sanitizePostAuthRedirect(
  redirectTo?: string | null,
  fallback = "/actions",
): string {
  if (
    !redirectTo ||
    !redirectTo.startsWith("/") ||
    redirectTo.startsWith("//")
  ) {
    return fallback;
  }

  if (redirectTo === "/login" || redirectTo.startsWith("/auth/callback")) {
    return fallback;
  }

  return redirectTo;
}

/**
 * Initiate Google OAuth sign-in flow.
 * Redirects the user to Google's consent screen.
 * After authentication, Google redirects back to /auth/callback.
 */
export async function signInWithGoogle(
  redirectTo?: string | null,
): Promise<void> {
  const nextPath = sanitizePostAuthRedirect(redirectTo);

  if (typeof window !== "undefined") {
    window.sessionStorage.setItem(OAUTH_REDIRECT_STORAGE_KEY, nextPath);
  }

  const { error } = await supabase.auth.signInWithOAuth({
    provider: "google",
    options: {
      redirectTo: `${window.location.origin}/auth/callback?next=${encodeURIComponent(nextPath)}`,
    },
  });
  if (error) throw error;
}

export async function register(userData: RegisterData): Promise<AuthUser> {
  try {
    // Sign up with Supabase
    const { data, error } = await supabase.auth.signUp({
      email: userData.email,
      password: userData.password,
      options: {
        data: {
          first_name: userData.firstName,
          last_name: userData.lastName,
        },
      },
    });

    if (error) throw error;
    if (!data.user) throw new Error("No user returned");

    const authUser = await buildAuthUser(data.user);

    if (typeof window !== "undefined") {
      setScopedItem(STORAGE_KEYS.USER, authUser);
    }

    logger?.info("User registered successfully", {
      userId: authUser.id,
      email: authUser.email,
    });

    return authUser;
  } catch (error) {
    logger?.error("Registration failed", error);
    throw error;
  }
}

export async function logout(): Promise<void> {
  try {
    await supabase.auth.signOut();
    clearTokens();
    logger?.info("User logged out");
  } catch (error) {
    logger?.warn("Logout request failed, clearing local data anyway", {
      error,
    });
    clearTokens();
  }
}

export async function refreshToken(): Promise<boolean> {
  try {
    const { data, error } = await supabase.auth.refreshSession();
    if (error) throw error;
    return !!data.session;
  } catch (error) {
    logger?.error("Token refresh failed", error);
    return false;
  }
}

export function getCurrentUser(): AuthUser | null {
  if (typeof window === "undefined") return null;

  try {
    return getScopedItem<AuthUser>(STORAGE_KEYS.USER);
  } catch (error) {
    logger?.error("Failed to parse stored user data", error);
    clearTokens();
    return null;
  }
}

export async function fetchCurrentUser(): Promise<AuthUser | null> {
  try {
    // Use getSession() rather than getUser() during bootstrap. getUser() makes
    // a network validation call serialized through supabase-js's cross-tab
    // auth lock (navigator.locks); if a prior tab/session leaked that lock
    // (crashed tab, navigation mid-refresh), getUser() waits on it forever
    // and the entire AuthProvider.initAuth stalls, which leaves AuthGate stuck
    // on the loading splash. getSession() reads from local storage without
    // touching that lock.
    //
    // Belt-and-suspenders: race against a 5s timeout so any future hang inside
    // the SDK can't pin the app on the splash — we'll just surface as "signed
    // out" and the user lands on /login instead of an infinite loader.
    const sessionResult = await Promise.race([
      supabase.auth.getSession(),
      new Promise<{ data: { session: null }; error: Error }>((resolve) =>
        setTimeout(
          () =>
            resolve({
              data: { session: null },
              error: new Error("supabase.auth.getSession timed out"),
            }),
          5000,
        ),
      ),
    ]);

    const {
      data: { session },
      error,
    } = sessionResult;

    if (error) {
      logger?.warn("getSession returned error during fetchCurrentUser", {
        error,
      });
      return null;
    }
    if (!session?.user) return null;

    const authUser = await buildAuthUser(session.user);

    // Update cached user
    if (typeof window !== "undefined") {
      setScopedItem(STORAGE_KEYS.USER, authUser);
    }

    return authUser;
  } catch (error) {
    logger?.error("Failed to fetch current user", error);
    return null;
  }
}

/**
 * Build an AuthUser from an already-validated Supabase session user, WITHOUT
 * calling getSession().
 *
 * Use this from `onAuthStateChange` handlers — they receive the session as an
 * argument, so there is no need to re-read it. Calling getSession() (e.g. via
 * fetchCurrentUser) from inside that callback re-enters auth-js's lock during
 * _initialize/_recoverAndRefresh and makes getSession resolve with an error —
 * the root cause of the long-standing `getSession returned error during
 * fetchCurrentUser` warning (registry: supabase_get_session_root_cause_diagnosis).
 *
 * Caches the result exactly like fetchCurrentUser so downstream reads are
 * consistent.
 */
export async function hydrateUserFromSession(sessionUser: {
  id: string;
  email?: string;
}): Promise<AuthUser> {
  const authUser = await buildAuthUser(sessionUser);

  if (typeof window !== "undefined") {
    setScopedItem(STORAGE_KEYS.USER, authUser);
  }

  return authUser;
}

export function hasRole(
  user: AuthUser | null,
  role: string | string[],
): boolean {
  if (!user) return false;
  const required = Array.isArray(role) ? role : [role];
  return required.some(
    (r) =>
      user.roles.includes(r.toLowerCase()) ||
      user.role.toLowerCase() === r.toLowerCase(),
  );
}

export function getEffectiveRoleForClient(
  user: AuthUser | null,
  clientId: string | null,
): UserRole {
  if (!user) return "viewer";
  if (!clientId || !user.memberships || user.memberships.length === 0) {
    return user.role;
  }

  const membership = user.memberships.find((m) => m.clientId === clientId);
  return membership?.role ?? user.role;
}

export function isAdminInAnyClient(user: AuthUser | null): boolean {
  if (!user) return false;
  if (user.role === "admin") return true;
  if (!user.memberships) return false;
  return user.memberships.some((m) => m.role === "admin");
}

export function isAuthenticated(): boolean {
  return getCurrentUser() !== null;
}

// Initialize auth on app start
export function initializeAuth(): void {
  // Supabase SDK handles initialization
  logger?.debug("Auth service initialized (Supabase)");
}
